<template>
  <b-dropdown
    variant="outline-info"
    no-caret
    :size="size"
    :disabled="disabled || isExportingGraph"
    menu-class="shadow"
    dropright
    toggle-class="squared-center-fa-btn d-print-none"
  >
    <template #button-content>
      <b-spinner
        v-if="isExportingGraph"
        small
        class="align-middle"
      />
      <icon
        v-else
        fixed-width
        icon="arrow-down-to-bracket"
      />
    </template>
    <!-- Download graph as PNG is shown to all users always -->
    <b-dropdown-item-button @click.stop="$emit('download', 'png')">
      <icon
        icon="file-image"
        class="text-danger mr-1"
      />
      &nbsp;{{ translate({ path: 'GLOBAL.DOWNLOAD' }) }}
    </b-dropdown-item-button>
    <!-- Copy graph to clipboard will be shown to all users (but not on Firefox, which does not support it) -->
    <b-dropdown-item-button
      v-if="shouldShowCopyToClipboardButton"
      @click.stop="$emit('copy')"
    >
      <icon
        icon="copy"
        class="text-info mr-1"
      />
      &nbsp;{{ translate({ path: 'GLOBAL.COPY' }) }}
    </b-dropdown-item-button>
    <!-- Download graph data as Excel is only shown for admins and only for certain graphs -->
    <b-dropdown-item-button
      v-if="isExcelExportImplemented && showExcelButton"
      @click.stop="$emit('download', 'xlsx')"
    >
      <icon
        icon="file-excel"
        class="text-success mr-1"
      />
      &nbsp;{{ translate({ path: 'GLOBAL.EXCEL' }) }}
    </b-dropdown-item-button>
  </b-dropdown>
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from 'vue';
import useTranslation from '@/composables/useTranslation';
import { useFeatureFlag } from '@/composables/useFeatureFlag';
import { UserPermission } from '@/constants/UserPermission';
import { useHasPermission } from '@/composables/usePermission';
import { useRoute } from 'vue-router';
import useRouteChecks from '@/composables/useRouteChecks';

/**
 * As of March 2024, all users should be able to download a graph as PNG or copy to clipboard.
 * Please note that copy to clipboard is NOT supported in Firefox.
 * Furthermore, the download Excel data option will only ever be shown to admins.
 */
export default defineComponent({
  name: 'ExportGraphButton',
  props: {
    size: {
      type: String as PropType<'sm' | 'md' | 'lg'>,
      required: false,
      default: 'sm',
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    isExportingGraph: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Excel export must be implemented for each graph individually because the data for each chart varies.
     * If it is implemented, set this prop to true so that admins may export Excel from the chart as desired.
     */
    isExcelExportImplemented: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * isCopyToClipboardImplemented should only be false when you have graphs with the following:
     * Graph using the amChart v4 library as current export function
     *
     * Copy to clipboard only supports v5 graphs.
     */
    isCopyToClipboardImplemented: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  emits: {
    download: (_format?: 'png' | 'xlsx') => true,
    copy: () => true,
  },
  setup(props) {
    const { translate } = useTranslation();
    const route = useRoute();
    const { isOnPortfolioConstruction } = useRouteChecks(route);
    const { isCopyToClipboardSupported } = useFeatureFlag();

    const shouldShowCopyToClipboardButton = computed(
      () => props.isCopyToClipboardImplemented && isCopyToClipboardSupported.value,
    );

    const excelPermission = computed((): UserPermission | null => {
      if (isOnPortfolioConstruction.value) {
        return UserPermission.PORTFOLIO_EXCEL;
      }

      return null;
    });

    const showExcelButton = useHasPermission(excelPermission);

    return {
      translate,
      shouldShowCopyToClipboardButton,
      showExcelButton,
    };
  },
});
</script>
